const WORKER_URL = 'https://pbw-reviews-worker.pbw-costaluis.workers.dev';

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str ?? '';
  return div.innerHTML;
}

function starsHtml(rating) {
  const filled = Math.round(rating);
  let out = '';
  for (let i = 1; i <= 5; i++) out += i <= filled ? '★' : '☆';
  return out;
}

const ICONS = {
  chevron: {
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>',
  },
  arrow: {
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>',
  },
};

function formatDate(raw) {
  if (!raw) return '';
  const date = new Date(raw.replace(' ', 'T') + 'Z');
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB');
}

class ProductReviewsCarousel extends HTMLElement {
  #observer = null;
  #loaded = false;

  connectedCallback() {
    // The reviews live on a Cloudflare worker, so fetching on connect meant a
    // third-party DNS and TLS handshake during page load for a section that is
    // below the fold on every template that uses it. Wait until it is nearly in
    // view. IntersectionObserver fires straight away for an element already
    // intersecting, so a short page still loads them immediately.
    if (typeof IntersectionObserver !== 'function') {
      this.#load();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#disconnectObserver();
        this.#load();
      },
      { rootMargin: '400px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#disconnectObserver();
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    try {
      const res = await fetch(`${WORKER_URL}/apps/reviews/store`);
      if (!res.ok) throw new Error('request failed');
      const data = await res.json();
      const reviews = data.reviews || [];

      if (!reviews.length) {
        this.hidden = true;
        return;
      }

      this.render(reviews, data);
    } catch {
      this.hidden = true;
    }
  }

  render(reviews, data) {
    const heading = this.dataset.heading || 'Customer Reviews';
    const cardsHtml = reviews
      .map(
        (r) => `
          <div class="pbw-reviews-carousel__card">
            <div class="pbw-reviews-carousel__stars">${starsHtml(r.rating)}</div>
            <div class="pbw-reviews-carousel__meta">
              <span class="pbw-reviews-carousel__name">${escapeHtml(r.customer_name || 'Anonymous')}</span>
              <span class="pbw-reviews-carousel__verified">Verified Buyer</span>
            </div>
            <div class="pbw-reviews-carousel__date">${formatDate(r.created_at)}</div>
            ${r.title ? `<p class="pbw-reviews-carousel__title">${escapeHtml(r.title)}</p>` : ''}
            ${r.body ? `<p class="pbw-reviews-carousel__body">${escapeHtml(r.body)}</p>` : ''}
          </div>
        `
      )
      .join('');

    const rawIconStyle = this.dataset.iconStyle || 'chevron';
    const isLarge = rawIconStyle.endsWith('_large');
    const iconGlyph = isLarge ? rawIconStyle.replace('_large', '') : rawIconStyle;
    const icons = ICONS[iconGlyph] || ICONS.chevron;
    const arrowClass = `pbw-reviews-carousel__arrow${isLarge ? ' pbw-reviews-carousel__arrow--large' : ''}`;

    this.innerHTML = `
      <h2 class="pbw-reviews-carousel__heading">${escapeHtml(heading)}</h2>
      <div class="pbw-reviews-carousel__track-wrap">
        <button type="button" class="${arrowClass}" data-direction="prev" aria-label="Previous reviews">${icons.prev}</button>
        <div class="pbw-reviews-carousel__track">${cardsHtml}</div>
        <button type="button" class="${arrowClass}" data-direction="next" aria-label="Next reviews">${icons.next}</button>
      </div>
      <div class="pbw-reviews-carousel__summary">
        <div class="pbw-reviews-carousel__average">
          <span class="pbw-reviews-carousel__average-stars">${starsHtml(data.average)}</span>
          <span class="pbw-reviews-carousel__average-value">${(data.average || 0).toFixed(2)} out of 5</span>
        </div>
      </div>
    `;

    const track = this.querySelector('.pbw-reviews-carousel__track');
    const prevButton = this.querySelector('[data-direction="prev"]');
    const nextButton = this.querySelector('[data-direction="next"]');

    const updateArrows = () => {
      prevButton.disabled = track.scrollLeft <= 0;
      nextButton.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    };

    const scrollByCard = (direction) => {
      const card = track.querySelector('.pbw-reviews-carousel__card');
      const amount = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
      track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    };

    prevButton.addEventListener('click', () => scrollByCard(-1));
    nextButton.addEventListener('click', () => scrollByCard(1));
    track.addEventListener('scroll', updateArrows);
    updateArrows();
  }
}

if (!customElements.get('product-reviews-carousel')) {
  customElements.define('product-reviews-carousel', ProductReviewsCarousel);
}
